import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createReply,
  fetchThread,
  moderate,
  reportPost,
  type ForumPost,
  type ForumThreadResponse,
} from "../api/forumApi";
import { ForumPostNode } from "../components/ForumPostNode";
import { ForumIdentityBar } from "../components/ForumIdentityBar";
import { useForumIdentity } from "../hooks/useForumIdentity";
import { formatRelativeTime } from "../utils/time";
import { showSuccess, showError, confirmDanger, promptText } from "../components/Dialog";

/**
 * One thread, rendered as a tree. Posts are stored flat with a parentId
 * (see forum-post.ts) and assembled into nesting here, which keeps the
 * stored shape simple and lets a moderator remove a post without
 * orphaning the replies underneath it.
 */
export function ForumThread() {
  const { threadId = "" } = useParams();
  const navigate = useNavigate();
  const identity = useForumIdentity();
  const [data, setData] = useState<ForumThreadResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const countedView = useRef(false);
  // Posts this tab just made that the server's read side may not be
  // showing yet. Netlify Blobs reads trail writes by a second or two (see
  // the note in forum-post.ts), so without this your own reply vanishes
  // for a beat after you send it. Each entry clears itself as soon as a
  // fetch comes back containing it.
  const [pending, setPending] = useState<ForumPost[]>([]);

  const load = useCallback(
    async (countView = false) => {
      try {
        const res = await fetchThread(threadId, identity.account, countView);
        setData(res);
        setPending((prev) => prev.filter((p) => !res.thread.posts.some((sp) => sp.id === p.id)));
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't load that thread.");
      } finally {
        setLoading(false);
      }
    },
    [threadId, identity.account]
  );

  useEffect(() => {
    setLoading(true);
    const first = !countedView.current;
    countedView.current = true;
    load(first);
  }, [load]);

  // A thread opened straight after being created can 404 while the write
  // propagates, so retry a few times before believing it.
  useEffect(() => {
    if (!error || data) return;
    const id = window.setTimeout(() => load(), 1500);
    return () => window.clearTimeout(id);
  }, [error, data, load]);

  // Light polling so replies from other people appear without a refresh.
  // 8s, not the chat's 1s — a message board doesn't need to feel live.
  useEffect(() => {
    const id = window.setInterval(() => load(), 8000);
    return () => window.clearInterval(id);
  }, [load]);

  // Posts arrive flat; group them by parent once per render so the
  // recursive renderer is a map lookup rather than a scan per node.
  const allPosts = useMemo(() => {
    const server = data?.thread.posts ?? [];
    const extra = pending.filter((p) => !server.some((sp) => sp.id === p.id));
    return [...server, ...extra];
  }, [data, pending]);

  const childrenByParent = useMemo(() => {
    const map = new Map<string, ForumPost[]>();
    for (const p of allPosts) {
      if (!p.parentId) continue;
      const list = map.get(p.parentId) ?? [];
      list.push(p);
      map.set(p.parentId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    }
    return map;
  }, [allPosts]);

  const op = allPosts.find((p) => p.id === "op");
  const summary = data?.summary ?? null;
  const locked = !!summary?.locked || !!data?.forumLocked;
  const canPost = !data?.requireWallet || identity.isWallet;

  const handleReply = useCallback(
    async (parentId: string, body: string) => {
      const res = await createReply({
        threadId,
        parentId,
        body,
        author: identity.displayName,
        isWallet: identity.isWallet,
      });
      setPending((prev) => [
        ...prev,
        {
          id: res.postId,
          parentId,
          author: identity.displayName || "anon",
          isWallet: identity.isWallet,
          body,
          createdAt: new Date().toISOString(),
        },
      ]);
      await load();
    },
    [threadId, identity.displayName, identity.isWallet, load]
  );

  const handleModerate = useCallback(
    async (action: string, payload: Record<string, unknown>) => {
      if (!identity.account) return;
      await moderate(action, { threadId, ...payload }, identity.account);
      if (action === "delete-thread") {
        navigate("/forum");
        return;
      }
      await load();
    },
    [identity.account, threadId, load, navigate]
  );

  const handleReport = useCallback(
    async (postId: string) => {
      const reason = await promptText({
        title: "Report this post",
        message: "What's wrong with it? A moderator will review your report.",
        placeholder: "Spam, abuse, scam link…",
        confirmLabel: "Send report",
      });
      if (reason === null) return;
      try {
        await reportPost({ threadId, postId, reason, reporter: identity.displayName || "anon" });
        showSuccess("A moderator will take a look.", "Report sent");
      } catch (err) {
        showError(err, "Couldn't send that report.");
      }
    },
    [threadId, identity.displayName]
  );

  async function submitTopLevelReply() {
    const body = draft.trim();
    if (!body) return;
    setPosting(true);
    setPostError(null);
    try {
      await handleReply("op", body);
      setDraft("");
    } catch (err) {
      setPostError(err instanceof Error ? err.message : "Couldn't post that.");
    } finally {
      setPosting(false);
    }
  }

  if (loading) return <div className="state-message">Loading thread…</div>;
  if (error || !data || !op) {
    return (
      <div className="page page--narrow">
        <div className="state-message state-message--error">{error ?? "Thread not found."}</div>
        <Link to="/forum" className="btn btn--outline">
          ← Back to the forum
        </Link>
      </div>
    );
  }

  const categoryName = data.categories.find((c) => c.id === data.thread.categoryId)?.name ?? data.thread.categoryId;

  return (
    <div className="page">
      <div className="forum-breadcrumb">
        <Link to="/forum" className="link-button">
          ← Forum
        </Link>
        <span className="forum-tag forum-tag--category">{categoryName}</span>
        {summary?.pinned && <span className="forum-tag forum-tag--pin">Pinned</span>}
        {summary?.locked && <span className="forum-tag">Locked</span>}
        {summary?.hidden && <span className="forum-tag forum-tag--hidden">Hidden</span>}
      </div>

      <div className="page__header">
        {editingTitle ? (
          <div className="forum-composer forum-composer--inline">
            <input type="text" maxLength={140} value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} />
            <div className="forum-composer__actions">
              <button
                className="btn btn--primary btn--small"
                onClick={async () => {
                  await handleModerate("edit-title", { title: titleDraft });
                  setEditingTitle(false);
                }}
              >
                Save title
              </button>
              <button className="btn btn--outline btn--small" onClick={() => setEditingTitle(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <h1>{data.thread.title}</h1>
        )}
        <p className="page__subtitle">
          Started by {op.author || "anon"} {formatRelativeTime(new Date(data.thread.createdAt).getTime())} ·{" "}
          {Math.max(allPosts.length - 1, 0)} {allPosts.length === 2 ? "reply" : "replies"}
          {summary?.views ? ` · ${summary.views} views` : ""}
        </p>
      </div>

      {identity.isAdmin && (
        <div className="forum-modbar">
          <span className="forum-modbar__label">Moderator</span>
          <button className="btn btn--outline btn--small" onClick={() => handleModerate(summary?.pinned ? "unpin" : "pin", {})}>
            {summary?.pinned ? "Unpin" : "Pin"}
          </button>
          <button className="btn btn--outline btn--small" onClick={() => handleModerate(summary?.locked ? "unlock" : "lock", {})}>
            {summary?.locked ? "Unlock" : "Lock"}
          </button>
          <button className="btn btn--outline btn--small" onClick={() => handleModerate(summary?.hidden ? "unhide" : "hide", {})}>
            {summary?.hidden ? "Unhide" : "Hide"}
          </button>
          <button
            className="btn btn--outline btn--small"
            onClick={() => {
              setTitleDraft(data.thread.title);
              setEditingTitle(true);
            }}
          >
            Rename
          </button>
          <select
            className="forum-modbar__select"
            value={data.thread.categoryId}
            onChange={(e) => handleModerate("move-thread", { categoryId: e.target.value })}
          >
            {data.categories.map((c) => (
              <option key={c.id} value={c.id}>
                Move to: {c.name}
              </option>
            ))}
          </select>
          <button
            className="btn btn--outline btn--small forum-post__danger"
            onClick={async () => {
              if (await confirmDanger("Delete this entire thread? This cannot be undone.")) handleModerate("delete-thread", {});
            }}
          >
            Delete thread
          </button>
        </div>
      )}

      <ForumPostNode
        post={op}
        childrenByParent={childrenByParent}
        depth={0}
        isAdmin={identity.isAdmin}
        locked={locked}
        canPost={canPost}
        onReply={handleReply}
        onModerate={handleModerate}
        onReport={handleReport}
      />

      {locked ? (
        <div className="callout callout--warning" style={{ marginTop: 24 }}>
          🔒 This thread is closed to new replies.
        </div>
      ) : !canPost ? (
        <div className="callout callout--warning" style={{ marginTop: 24 }}>
          Posting is currently limited to connected WAX wallets.
        </div>
      ) : (
        <div className="forum-composer forum-composer--reply">
          <ForumIdentityBar identity={identity} />
          <textarea
            rows={5}
            maxLength={5000}
            placeholder="Add to the discussion…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          {postError && <div className="forum-inline-error">{postError}</div>}
          <div className="forum-composer__actions">
            <button className="btn btn--primary" onClick={submitTopLevelReply} disabled={posting || !draft.trim()}>
              {posting ? "Posting…" : "Post reply"}
            </button>
            <span className="forum-composer__hint">Reply to a specific post with its own Reply button.</span>
          </div>
        </div>
      )}
    </div>
  );
}
